import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import Loadable from 'react-loadable';
import Footer from '@zola/zola-ui/src/components/Footer/Footer';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import { BusinessUnit } from '@zola-helpers/client/dist/es/@types';
import detectBusinessUnit from '@zola-helpers/client/dist/es/util/detectBusinessUnit';
import { showModal, hideModal } from 'actions/ModalActions';
import Loader from 'components/common/ui/Loader';

const {
  EVENTS: {
    NAV_LOADED,
    OPEN_MODAL_EVENT,
    MODAL_TYPE_PUBLISH_REGISTRY,
    MODAL_TYPE_SET_SHIPPING_ADDRESS,
    MODAL_TYPE_AUTH,
    MODAL_TYPE_BOOKING_STATUS_REMINDER,
  },
} = NAV_DATA;

const AuthModal = Loadable({
  loader: () => import('./auth/AuthModal'),
  loading: Loader,
});

const CartModal = Loadable({
  loader: () => import('./modals/cart/CartModal'),
  loading: Loader,
});

const PublishRegistryModal = Loadable({
  loader: () => import('./modals/registry/PublishRegistryModal'),
  loading: Loader,
});

const SetShippingAddressModal = Loadable({
  loader: () => import('./modals/registry/SetShippingAddressModal'),
  loading: Loader,
});

const ShippingExclusionModal = Loadable({
  loader: () => import('./modals/registry/ShippingExclusionModal'),
  loading: Loader,
});

const BookingStatusReminderModal = Loadable({
  loader: () => import('./modals/marketplace/BookingStatusReminderModal'),
  loading: Loader,
});

window.zolaNav = window.zolaNav || {};

const MODAL_COMPONENTS = {
  AUTH: AuthModal,
  CART_MODAL: CartModal,
  PUBLISH_REGISTRY: PublishRegistryModal,
  SET_SHIPPING_ADDRESS: SetShippingAddressModal,
  SHIPPING_EXCLUSION: ShippingExclusionModal,
  /* other modals */
  BOOKING_STATUS_REMINDER: BookingStatusReminderModal,
};

class ModalRoot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      previousActiveElement: null,
    };

    this.onBackgroundClick = this.onBackgroundClick.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.openModalEventHandler = this.openModalEventHandler.bind(this);
    this.onHideModal = this.onHideModal.bind(this);
  }

  componentDidMount() {
    window.addEventListener(OPEN_MODAL_EVENT, this.openModalEventHandler, false);
    window.dispatchEvent(new CustomEvent(NAV_LOADED));
  }

  componentDidUpdate(prevProps) {
    const { isVisible } = this.props;
    if (!prevProps.isVisible && isVisible) this.onShow();
    if (prevProps.isVisible && !isVisible) this.onHide();
  }

  componentWillUnmount() {
    window.removeEventListener(OPEN_MODAL_EVENT, this.openModalEventHandler);
  }

  onShow() {
    const { previousActiveElement } = this.state;
    const { modalProps } = this.props;
    if (!previousActiveElement) {
      this.setState({
        previousActiveElement: document.activeElement,
      });
    }
    if (document && document.body) {
      // const orig = document.body.className;
      // document.body.className = `${orig} modal-open`.trim();
      document.body.classList.add('modal-open');
    }
    if (modalProps.isFullPage || !modalProps.hideClose) {
      document.addEventListener('keydown', this.onEscape);
    }
    window.zolaNav.modalOpen = true;
  }

  onHide() {
    if (document && document.body) {
      // document.body.className = document.body.className.replace(/ ?modal-open/g, '');
      document.body.classList.remove('modal-open');
    }
    document.removeEventListener('keydown', this.onEscape);
    window.zolaNav.modalOpen = false;

    const { previousActiveElement } = this.state;
    if (previousActiveElement) {
      previousActiveElement.focus();
      this.setState({ previousActiveElement: null });
    }
  }

  onBackgroundClick(e) {
    const { modalProps } = this.props;
    if (e.target === e.currentTarget && !modalProps.hideClose) this.onHideModal();
  }

  onEscape({ keyCode }) {
    if (keyCode === 27) this.onHideModal();
  }

  onHideModal() {
    const {
      modalProps: { closeCallback },
      hideModalFn,
    } = this.props;

    if (closeCallback instanceof Function) {
      closeCallback();
    }

    hideModalFn();
  }

  openModalEventHandler(event) {
    const { openModal } = this.props;
    const { detail } = event;
    if (detail === 'CART') {
      // TODO: refactor cart to implement this open modal listener
      return;
    }
    const { type } = detail;
    // TODO: refactor so that detail: {type, modalProps, modalOptions}
    if (type === MODAL_TYPE_PUBLISH_REGISTRY) {
      const { slug } = detail;
      const modalOptions = { size: 'md', ariaLabel: 'Registry Published' };
      openModal(MODAL_TYPE_PUBLISH_REGISTRY, { slug }, modalOptions);
    } else if (type === MODAL_TYPE_SET_SHIPPING_ADDRESS) {
      const modalOptions = { size: 'md', ariaLabel: 'Set Shipping Address' };
      openModal(MODAL_TYPE_SET_SHIPPING_ADDRESS, {}, modalOptions);
    } else if (MODAL_TYPE_AUTH) {
      const { modalProps, modalOptions } = detail;
      setTimeout(() => {
        openModal(MODAL_TYPE_AUTH, modalProps, modalOptions);
      });
    }
  }

  render() {
    const { modalType, modalProps, modalOptions } = this.props;
    if (!modalType) {
      return null;
    }
    const modalSize = (modalOptions && modalOptions.size) || 'lg';
    const SpecificModal = MODAL_COMPONENTS[modalType];
    return (
      <div className="modal-root">
        <FocusTrap
          active={!modalProps.hideClose}
          focusTrapOptions={{
            allowOutsideClick: true,
          }}
        >
          <div
            id="modal"
            role="dialog"
            aria-modal="true"
            aria-label={modalOptions.ariaLabel || 'Zola Modal'}
            className="modal in"
            style={{ zIndex: 1050, display: 'block' }}
            onClick={this.onBackgroundClick}
          >
            <div
              className={cx('modal-dialog', `modal-${modalSize}`, {
                'modal--AUTH': modalType === MODAL_TYPE_AUTH,
                'modal--MARKETPLACE': modalType === MODAL_TYPE_BOOKING_STATUS_REMINDER,
              })}
            >
              <div className="modal-content" style={{ ...modalOptions.styleOverride }}>
                {!modalProps.hideClose && (
                  <button
                    aria-label="Close"
                    type="button"
                    className="modal-close"
                    onClick={this.onHideModal}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                )}
                <SpecificModal {...modalProps} hideModalFn={this.onHideModal} />
              </div>
            </div>
            {detectBusinessUnit() === BusinessUnit.STORE && modalType === MODAL_TYPE_AUTH && (
              <Footer isWebStore />
            )}
          </div>
        </FocusTrap>
        {!modalProps.isFullPage && <div className="modal-backdrop in" />}
      </div>
    );
  }
}

ModalRoot.propTypes = {
  openModal: PropTypes.func.isRequired,
  modalType: PropTypes.string,
  modalProps: PropTypes.shape({
    callback: PropTypes.func,
    hideClose: PropTypes.bool,
    isFullPage: PropTypes.bool,
    closeCallback: PropTypes.func,
  }),
  hideModalFn: PropTypes.func.isRequired,
  modalOptions: PropTypes.shape({
    size: PropTypes.string,
    ariaLabel: PropTypes.string,
    styleOverride: PropTypes.shape({}),
  }),
  isVisible: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  ...state.modal,
});

const mapDispatchToProps = {
  hideModalFn: hideModal,
  openModal: showModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalRoot);
